import { axios } from 'api'
import commentAxios from 'api/commentsAxios'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button, Nav } from 'react-bootstrap'
import ReportsTable from './ReportsTable'

const TableWrapper = styled.div`
  padding: 1.5rem;
  padding-top: 2rem;
  position: relative;
  th {
    color: #6c757d;
    cursor: default;
    user-select: none;
    white-space: nowrap;
  }
  .td-date {
    white-space: nowrap;
  }
  .blue,
  .pointer {
    color: #3985b9;
    user-select: none;
    cursor: pointer;
  }
  button {
    user-select: none;
  }
`

const Main = styled.div`
  .flex-box {
    display: flex;
    gap: 8px;
    align-items: center;
    button {
      border-radius: 6px !important;
      height: 20px;
      width: 20px;
    }
  }
  p {
    margin: 0;
  }
  .p-0 {
    padding: 0 8px;
    &.open {
      padding: 8px;
    }
  }
  .absolute {
    top: -25px;
    position: absolute;
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 3rem;
    .flex-box {
      margin-top: 20px;
      p {
        font-weight: 600;
      }
    }
    .select-sort {
      label {
        margin-bottom: 0;
      }
      select {
        padding: 6px;
      }
    }
  }
`

const Arrow = ({ desc = true, show = false }) =>
  show && <span>{desc ? '⬇️' : '⬆️'}</span>

const TABS = [
  {
    id: 'CLASS',
    title: 'classes'
  },
  {
    id: 'SERIES',
    title: 'series'
  },
  {
    id: 'REPORTS',
    title: 'reports'
  }
]

const ContentTable = () => {
  const [sortConfig, setSortConfig] = useState({
    key: 'likes',
    desc: true,
    lastKey: null,
    loadMore: false,
    content_type: '',
    sortKey: 'newest'
  })
  const [data, setData] = useState([])
  const [saves, setSaves] = useState({})
  const [lastKey, setLastKey] = useState(null)
  const [loading, setLoading] = useState(false)

  const updateQueryParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)
    let tab = String(urlParams.get('tab'))
    tab = ['class', 'series', 'reports'].includes(String(tab).toLowerCase())
      ? tab
      : 'class'

    setSortConfig((state) => ({
      ...state,
      content_type: tab?.toUpperCase()
    }))
    window.history.pushState({}, null, `/admin/engagement?tab=${tab}`)
  }, [])

  useEffect(() => {
    window.addEventListener('popstate', updateQueryParams)
    return () => {
      window.removeEventListener('popstate', updateQueryParams)
    }
  }, [updateQueryParams])

  useEffect(() => {
    updateQueryParams()
  }, [updateQueryParams])

  const handleTabChange = (content_type) => {
    if (sortConfig.content_type === content_type) return
    setLastKey(null)
    window.history.pushState(
      {},
      null,
      `/admin/engagement?tab=${content_type?.toLowerCase()}`
    )
    setData([])
    setSaves({})
    setSortConfig((state) => ({
      ...state,
      desc: true,
      lastKey: null,
      loadMore: false,
      content_type,
      key: 'likes',
      sortKey: 'newest'
    }))
  }

  const toggleSort = (key) => {
    setLastKey(null)
    setSortConfig((state) => {
      if (key === state.key) {
        return { ...state, desc: !state.desc, lastKey: null, loadMore: false }
      }
      return { ...state, key, desc: true, lastKey: null, loadMore: false }
    })
  }

  const fetchReport = useCallback(
    async ({
      sortKey = 'newest',
      lastKey = null,
      loadMore = false,
      deleted = false
    }) => {
      try {
        const res = await commentAxios.post('/admin/reports', {
          limit: 10,
          sort: sortKey,
          last_evaluated_key: lastKey,
          deleted
        })
        const data = res.data?.reports || []
        setData((state) => {
          if (loadMore) {
            return [...state, ...data]
          }
          return data
        })

        setLastKey(res?.data?.lastEvaluatedKey)
      } catch (err) {
        console.log(err)
      }
    },
    []
  )

  const fetchData = useCallback(
    async ({
      key = 'likes',
      desc = true,
      lastKey,
      loadMore = false,
      content_type = 'CLASS',
      deleted,
      sortKey
    }) => {
      if (!content_type) return
      if (content_type === 'REPORTS') {
        fetchReport({ lastKey, loadMore, sortKey, deleted })
        return
      }
      try {
        setLoading(true)
        const res = await commentAxios.post('/admin/content', {
          sort: key,
          content_type,
          direction: desc ? 'desc' : 'asc',
          limit: 10,
          last_evaluated_key: lastKey
        })
        const data = res.data?.content || []
        setData((state) => {
          if (loadMore) {
            return [...state, ...data]
          }
          return data
        })
        setLastKey(res?.data?.lastEvaluatedKey)
        const ids = data?.map((i) => i?.id)?.filter(Boolean) || []
        if (ids.length > 0) {
          const saves_res = await axios.post(
            '/engagement/get_saves',
            { ids, content_type },
            { baseURL: '/admin' }
          )

          const newSaved = saves_res.data?.saved || {}
          setSaves((state) => ({ ...state, ...newSaved }))
        }
      } catch (err) {
        console.log(err)
        setData([])
        setSaves({})
      } finally {
        setLoading(false)
      }
    },
    [fetchReport]
  )

  useEffect(() => {
    fetchData(sortConfig)
  }, [fetchData, sortConfig])

  const handlePagination = () => {
    setSortConfig((prev) => ({
      ...prev,
      lastKey,
      loadMore: true
    }))
  }

  const handleDelete = ({
    comment_id = '',
    reply_id = '',
    content_id = '',
    content_type = ''
  }) => {
    commentAxios
      .post('/content-interaction/delete', {
        content_type,
        content_id,
        comment_id,
        ...(reply_id && {
          reply_id
        })
      })
      .then((res) => {
        if (res.data?.success) {
          setLastKey(null)
          setSortConfig((state) => ({
            ...state,
            lastKey: null,
            loadMore: false
          }))
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

  return (
    <Main>
      <Nav className='nav-tabs nav' role='tablist'>
        {TABS.map((tab) => (
          <li
            key={tab.id?.toLowerCase()}
            onClick={() => handleTabChange(tab.id)}
            className={tab.id === sortConfig.content_type ? 'active' : ''}
          >
            <a role='button'>{tab.title}</a>
          </li>
        ))}
      </Nav>

      <TableWrapper>
        {sortConfig.content_type === 'REPORTS' ? (
          <ReportsTable
            setSortConfig={setSortConfig}
            handleDelete={handleDelete}
            sortConfig={sortConfig}
            data={data}
          />
        ) : (
          <table className='table table-bordered table-striped'>
            <thead className='table-light'>
              <tr>
                {[
                  { key: 'title', label: 'Name' },
                  {
                    label: 'Instructor (s)'
                  },
                  { key: 'comments', label: 'Comments' },
                  { key: 'likes', label: 'Likes' },
                  { key: 'dislikes', label: 'Dislikes' }
                ].map((item, ix) => (
                  <th
                    key={ix}
                    {...(item.key && {
                      onClick: () => toggleSort(item.key),
                      className: 'pointer'
                    })}
                    role='button'
                  >
                    {item.label || item.key}
                    {'  '}
                    <Arrow
                      desc={sortConfig.desc}
                      show={sortConfig.key === item.key || ''}
                    />
                  </th>
                ))}

                <th>Saves</th>
                <th>Manage</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  <td>{row.title}</td>
                  <td>{row.instructors}</td>
                  <td>{row.commentCount}</td>
                  <td>{row.likes}</td>
                  <td>{row.dislikes}</td>
                  <td>{saves[row.id] || 0}</td>
                  <td>
                    <a
                      className='blue'
                      href={`/admin/engagement/${
                        sortConfig.content_type === 'CLASS' ? 'class' : 'series'
                      }/${row.id}`}
                    >
                      EDIT
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className='text-left my-3'>
          <Button
            classID='load-more'
            variant='primary'
            onClick={handlePagination}
            disabled={!Boolean(lastKey) || loading}
          >
            {loading
              ? 'Loading...'
              : Boolean(lastKey)
              ? 'Load More'
              : 'No More Items'}
          </Button>
        </div>
      </TableWrapper>
    </Main>
  )
}

export default ContentTable
